import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/react-hooks'
import * as Query from '../../../api/gql/query'
import { useRouter } from '../../../hooks'
import { useDebounce } from '../../../hooks/useDebounce'
import { useModuleActions } from '../../../hooks/useMyPermissions'
import { useRfqMutations } from '../hooks/useRfqMutations'
import { describeCostControlError } from '../lib/errors'
import { todayIso } from '../lib/invoices'
import { moneyLocale } from '../lib/money'
import { EMPTY_RFQ_FILTER, isRfqFiltered, rfqAffordances, toRfqFilter, type RfqFilterState } from '../lib/rfq'
import { buildTree } from '../lib/tree'
import type { CostContract, CostControlView, CostRfq } from '../types'
import RfqDialog from './RfqDialog'
import RfqsTable from './RfqsTable'
import RfqsTabSkeleton from './RfqsTabSkeleton'
import RfqsToolbar from './RfqsToolbar'

interface Props { projectId: string }

/** Part D spec §8: the project's RFQs with a status filter and a search; a new RFQ opens its detail. */
const RfqsTab: React.FC<Props> = ({ projectId }) => {
  const { t, i18n } = useTranslation()
  const locale = moneyLocale(i18n.language)
  const { history } = useRouter()
  const actions = useModuleActions('COST_CONTROL', projectId)
  const af = rfqAffordances(actions, null)
  const mutations = useRfqMutations(projectId)
  const [filter, setFilter] = useState<RfqFilterState>(EMPTY_RFQ_FILTER)
  const [search] = useDebounce(filter.search, 300)
  const [creating, setCreating] = useState(false)
  const { data, loading, error } = useQuery<{ costRfqs: CostRfq[] }>(Query.COST_RFQS, {
    variables: { projectId, filter: toRfqFilter(filter, search) },
    fetchPolicy: 'cache-and-network',
    context: { handlesErrors: true },
  })
  const { data: contractData } = useQuery<{ costContracts: CostContract[] }>(Query.COST_CONTRACTS, {
    variables: { projectId, filter: {} },
    skip: !af.create,
    fetchPolicy: 'cache-and-network',
    context: { handlesErrors: true },
  })
  const { data: viewData } = useQuery<{ costControl: CostControlView }>(Query.COST_CONTROL, {
    variables: { projectId },
    fetchPolicy: 'cache-first',
    context: { handlesErrors: true },
  })
  const currency = viewData?.costControl.settings.currency ?? 'CZK'
  const tree = useMemo(() => buildTree(viewData?.costControl.nodes ?? []), [viewData])
  const rows = data?.costRfqs
  const open = (id: string) => history.push(`/project/${projectId}/cost-control/rfqs/${id}`)

  if (!rows && loading) return <RfqsTabSkeleton />

  return (
    <div className="space-y-2">
      <RfqsToolbar count={rows?.length ?? 0} filter={filter} onFilterChange={setFilter} canCreate={af.create} onCreate={() => setCreating(true)} />
      {!rows && error && <p className="text-sm text-destructive">{describeCostControlError(error.message, t)}</p>}
      {rows && rows.length === 0 && (
        <div className="rounded-md border p-8 text-center text-sm text-muted-foreground">
          {t(isRfqFiltered(filter) ? 'CostControl_rfqs_empty_filtered' : 'CostControl_rfqs_empty')}
        </div>
      )}
      {rows && rows.length > 0 && <RfqsTable rows={rows} currency={currency} locale={locale} today={todayIso()} onOpen={open} />}
      <RfqDialog
        open={creating}
        mode="create"
        contracts={contractData?.costContracts ?? []}
        tree={tree}
        onOpenChange={setCreating}
        onSubmit={async (input) => {
          const res = await mutations.create(input)
          if (res.ok) open(res.data.createCostRfq.id)
          return res.ok
        }}
      />
    </div>
  )
}
export default RfqsTab
